import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { getActivityReports, deleteActivityReport, addActivityReport } from '../../services/activityReportService';

const ActivityReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [newReport, setNewReport] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    file: null
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await getActivityReports();
      // Tarihe göre sırala (yeniden eskiye)
      const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
      setReports(sortedData);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Faaliyet raporları yüklenirken bir hata oluştu.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedReport) return;
    
    try {
      setLoading(true);
      const response = await deleteActivityReport(selectedReport.guid);
      
      if (response?.isSuccess) {
        await fetchReports();
        setShowDeleteModal(false);
      } else {
        setError('Rapor silinirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Rapor silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null); // Her denemede hata mesajını sıfırla
      
      const reportData = {
        name: newReport.name,
        date: newReport.date,
        file: newReport.file
      };

      const response = await addActivityReport(reportData);
      
      if (response?.isSuccess) {
        await fetchReports();
        setShowAddModal(false);
        setNewReport({
          name: '',
          date: new Date().toISOString().split('T')[0],
          file: null
        });
      } else {
        setError('Rapor eklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Add error:', err);
      // Hata mesajını doğrudan göster
      setError(err.message || 'Rapor eklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Faaliyet Raporları ({reports.length})</h2>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          Yeni Rapor Ekle
        </Button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <Row xs={1} md={2} lg={3} className="g-4">
        {reports.map((report) => (
          <Col key={report.guid}>
            <Card>
              <Card.Body>
                <Card.Title>{report.name}</Card.Title>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button
                    variant="primary"
                    href={report.file}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    İndir
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setSelectedReport(report);
                      setShowDeleteModal(true);
                    }}
                  >
                    Sil
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Rapor Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu raporu silmek istediğinizden emin misiniz?
          <br />
          <strong>{selectedReport?.name}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => {
        setShowAddModal(false);
        setError(null); // Modalı kapatmadan önce hata mesajını temizle
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Yeni Rapor Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}
          <Form onSubmit={handleAdd}>
            <Form.Group className="mb-3">
              <Form.Label>Rapor Adı</Form.Label>
              <Form.Control
                type="text"
                value={newReport.name}
                onChange={(e) => setNewReport(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tarih</Form.Label>
              <Form.Control
                type="date"
                value={newReport.date}
                onChange={(e) => setNewReport(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Dosya</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={(e) => setNewReport(prev => ({ ...prev, file: e.target.files[0] }))}
                required
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowAddModal(false)}>
                İptal
              </Button>
              <Button variant="primary" type="submit">
                Ekle
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ActivityReportList;
